import { useCallback, useEffect } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';

/**
 * Hook to manage project data fetching for Projects page
 * @param {Object} params - Hook parameters
 * @param {number} params.page - Current page
 * @param {number} params.rowsPerPage - Rows per page
 * @param {string} params.searchQuery - Search query string
 * @param {Function} params.setProjects - Setter for projects
 * @param {Function} params.setTotalProjects - Setter for total projects
 * @param {Function} params.setLoading - Setter for loading state
 * @returns {Function} Fetch projects function
 */
export const useProjectsPageData = ({
  page,
  rowsPerPage,
  searchQuery,
  setProjects,
  setTotalProjects,
  setLoading,
}) => {
  const { showSnackbar } = useSnackbar();

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      };

      // Add search parameter only if present and not empty
      const trimmedSearch = searchQuery?.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response = await api.get('/projects', { params });
      
      if (response.data && response.data.data) {
        setProjects(response.data.data.projects || []);
        setTotalProjects(response.data.data.total || 0);
      }
    } catch (error) {
      // console.error('Error fetching projects:', error);
      showSnackbar('Failed to fetch projects. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, setProjects, setTotalProjects, setLoading, showSnackbar]);

  // Fetch projects when page, rowsPerPage, or searchQuery changes
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    fetchProjects,
  };
};
